import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Check, ChevronDown, History, Loader2 } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { FormLifecycleBadge } from '@/components/form-builder/FormLifecycleBadge'
import { formsApi } from '@/api/formsApi'
import { formUniqueId } from '@/lib/versioning'
import { familyVersionLabel, type FormVersionIndicatorState } from '@/lib/formVersionIndicator'
import { useOptionalTenant } from '@/providers/TenantProvider'
import { cn } from '@/lib/utils'

/**
 * Which version of the form is open, in the Form Builder header (z8nrz7dmb1).
 *
 * The FORM's own version — `FRM-014-V4 · ACTIVE` — deliberately apart from the
 * template selector, which is about the template the form is authored against
 * (AC1.5). Display-only: rendering it writes nothing and touches no dirty
 * state (AC1.4).
 *
 * Clicking it lists every version of the form's family, highest first, INACTIVE
 * included (AC2.1–AC2.3, AC2.10). Picking the open one just closes the list
 * (AC2.8); picking another hands its id to `onOpenVersion`, which opens it
 * read-only — the host owns that, including the unsaved-changes prompt.
 */
export function FormVersionIndicator({
  state,
  definitionId,
  currentId,
  onOpenVersion,
}: {
  state: FormVersionIndicatorState
  /** Any saved definition of the family — what the version list is read through. */
  definitionId: string | undefined
  /** The version the author is on: marked "Current" in the list. */
  currentId: string | undefined
  onOpenVersion: (id: string) => void
}) {
  const [open, setOpen] = useState(false)
  const tenantId = useOptionalTenant()?.selectedTenantId ?? null
  const versions = useQuery({
    queryKey: ['form-family-versions', tenantId, definitionId],
    queryFn: () => formsApi.listFamilyVersions(definitionId as string),
    enabled: open && !!definitionId,
    staleTime: 0,
  })

  if (state.kind === 'unsaved') {
    return (
      <span className='shrink-0 text-xs text-muted-foreground' data-testid='form-version-indicator'>
        {state.text}
      </span>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type='button'
          data-testid='form-version-indicator'
          aria-label={`Form version: ${state.text}. Show all versions`}
          className='flex shrink-0 items-center gap-1.5 rounded-md border px-2 py-1 text-xs hover:bg-muted'
        >
          <History className='h-3.5 w-3.5 text-muted-foreground' />
          <span className={cn('font-mono', state.kind === 'unavailable' && 'text-muted-foreground')}>
            {state.text}
          </span>
          {state.kind === 'version' && (
            <>
              <span className='text-muted-foreground'>·</span>
              <FormLifecycleBadge active={state.active} />
            </>
          )}
          <ChevronDown className='h-3 w-3 text-muted-foreground' />
        </button>
      </PopoverTrigger>
      <PopoverContent align='start' className='w-96 p-2'>
        <p className='px-2 pb-2 pt-1 text-xs font-semibold text-muted-foreground'>Versions of this form</p>
        {versions.isLoading ? (
          <p className='flex items-center gap-2 px-2 py-3 text-xs text-muted-foreground'>
            <Loader2 className='h-3.5 w-3.5 animate-spin' /> Loading versions…
          </p>
        ) : versions.isError ? (
          <p className='px-2 py-3 text-xs text-destructive'>The versions could not be loaded.</p>
        ) : (
          <ul role='listbox' aria-label='Form versions' className='max-h-80 space-y-0.5 overflow-y-auto'>
            {(versions.data ?? []).map(v => {
              const isCurrent = v.id === currentId
              return (
                <li key={v.id}>
                  <button
                    type='button'
                    role='option'
                    aria-selected={isCurrent}
                    onClick={() => {
                      setOpen(false)
                      if (!isCurrent) onOpenVersion(v.id)
                    }}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs hover:bg-muted',
                      isCurrent && 'bg-muted/60',
                    )}
                  >
                    <span className='font-mono'>{formUniqueId(v)}</span>
                    <span className='text-muted-foreground'>{familyVersionLabel(v)}</span>
                    <FormLifecycleBadge active={v.active} />
                    <span className='ml-auto text-muted-foreground'>
                      {v.lastVersionUpdateAt ? new Date(v.lastVersionUpdateAt).toLocaleDateString() : '—'}
                    </span>
                    {isCurrent && (
                      <span className='flex items-center gap-0.5 font-medium text-primary'>
                        <Check className='h-3 w-3' /> Current
                      </span>
                    )}
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  )
}
